<template>
  <div
    :class="[
      'p-blockui-wrapper',
      className,
      {
        'p-blockui-responsive': responsive,
        'p-blockui-rtl': dir === 'rtl'
      }
    ]"
    :style="style"
  >
    <BlockUI
      :blocked="blocked"
      :fullScreen="fullScreen"
      :baseZIndex="baseZIndex"
      :autoZIndex="autoZIndex"
      v-bind="$attrs"
      @block="onBlock"
      @unblock="onUnblock"
    >
      <div
        :class="containerClass"
        :role="blocked ? 'alert' : undefined"
        :aria-busy="blocked"
        :aria-label="ariaLabel"
      >
        <slot></slot>
      </div>
    </BlockUI>
    <Transition name="p-blockui">
      <div
        v-if="blocked"
        class="p-blockui-overlay"
        :class="{ 'p-blockui-overlay-fullscreen': fullScreen }"
        :style="overlayStyle"
      >
        <div
          class="p-blockui-content"
          :aria-label="ariaLoadingMessage"
        >
          <slot name="content">
            <ONEProgressSpinner />
          </slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import BlockUI from 'primevue/blockui'
import ONEProgressSpinner from '../ONEProgressSpinner/ONEProgressSpinner.vue'
import type { ONEBlockUIProps } from './types'

export default defineComponent({
  name: 'ONEBlockUI',
  components: {
    BlockUI,
    ONEProgressSpinner
  },
  inheritAttrs: false,
  emits: ['update:blocked', 'block', 'unblock']
})
</script>

<script setup lang="ts">
import { computed, watch } from 'vue'

const emit = defineEmits<{
  (e: 'update:blocked', value: boolean): void
  (e: 'block'): void
  (e: 'unblock'): void
}>()

const props = withDefaults(defineProps<ONEBlockUIProps>(), {
  blocked: false,
  fullScreen: false,
  baseZIndex: 0,
  autoZIndex: true,
  className: undefined,
  style: undefined,
  ariaLabel: 'Loading',
  ariaLoadingMessage: 'Please wait while content is loading',
  responsive: true,
  dir: 'ltr'
})

const containerClass = computed(() => [
  'p-blockui-container',
  {
    'p-blockui-active': props.blocked,
    'p-blockui-fullscreen': props.fullScreen
  }
])

const overlayStyle = computed(() => ({
  zIndex: props.autoZIndex ? props.baseZIndex + 1 : props.baseZIndex
}))

const onBlock = () => {
  emit('block')
}

const onUnblock = () => {
  emit('unblock')
}

watch(() => props.blocked, (newValue) => {
  emit('update:blocked', newValue)
})
</script>

<style lang="scss">
.p-blockui {
  &-wrapper {
    position: relative;
  }

  &-container {
    position: relative;
  }

  &-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;

    &-fullscreen {
      position: fixed;
    }
  }

  &-content {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 3rem;
    padding: 1rem;
    border-radius: 4px;
  }

  &-enter-active,
  &-leave-active {
    transition: opacity 0.2s ease;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }

  &-responsive {
    @media screen and (max-width: 576px) {
      .p-blockui-content {
        transform: scale(0.8);
      }
    }
  }

  &-rtl {
    direction: rtl;
    .p-blockui-content {
      transform-origin: right center;
    }
  }
}
</style>
